import type { TimelineElement } from '../../../context/TimelineContext'
import type {
  AnalysisAssigneeLoad,
  AnalysisBlockerChain,
  AnalysisCohortSlice,
  AnalysisDriftEvent,
  AnalysisHealth,
  AnalysisHealthSubScores,
  AnalysisReadinessRow,
  AnalysisRecommendation,
  AnalysisSnapshot,
  AnalysisTimeRangeKey,
  AnalysisTrend,
} from '../../../types/analysis'
import { buildAssigneeClusters } from '../../../utils/analysisCohorts'
import type { ChecklistItem } from '../../../types/timeline'

type WorkItem = {
  id: string
  title: string
  type: 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST'
  parentId: string | null
  endDate: string | null
  startDate: string | null
  completed: boolean
  progress: number
  blocked: boolean
  blockerIds: string[]
  dependencyIds: string[]
  assigneeId: string | null
  assigneeLabel: string | null
  requiresProof: boolean
  budgetLimit: number
  estimatedCost: number
  stockTouched: boolean
  formsTouched: boolean
  integrationsTouched: boolean
  priority: number
  lastUpdatedAt: Date | null
  aiTouched: boolean
  sourceElementId: string
}

type DerivationChecklistItem = ChecklistItem | import('../../../types/schema').ChecklistItem

type AnalysisDerivationOptions = {
  now?: Date
  timeRange?: AnalysisTimeRangeKey
  includeArchived?: boolean
  resolveMemberLabel?: (uid: string) => string | null
  memberRoles?: Map<string, string>
  cohort?: AnalysisCohortSlice | null
  checklistItems?: DerivationChecklistItem[]
}

export type CohortAnalysisSnapshots = { sidecar: AnalysisSnapshot; middle: AnalysisSnapshot }

const MS_PER_DAY = 86_400_000

const clampScore = (value: number) => Math.max(0, Math.min(100, Math.round(value)))

const toDate = (value: unknown): Date | null => {
  if (!value) return null
  if (value instanceof Date && Number.isFinite(value.getTime())) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isFinite(parsed.getTime()) ? parsed : null
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
    const parsed = value.toDate()
    return parsed instanceof Date && Number.isFinite(parsed.getTime()) ? parsed : null
  }
  return null
}

const startOfDay = (date: Date) => {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

const daysLate = (endDate: string | null, now: Date) => {
  const end = toDate(endDate)
  if (!end) return 0
  return Math.max(0, Math.ceil((startOfDay(now).getTime() - startOfDay(end).getTime()) / MS_PER_DAY))
}

const timeRangeDays = (range: AnalysisTimeRangeKey) => {
  if (range === '24H') return 1
  if (range === '7D') return 7
  if (range === '30D') return 30
  if (range === '90D') return 90
  if (range === '12M') return 365
  const exhaustive: never = range
  throw new Error(`Unsupported analysis time range: ${exhaustive}`)
}

const bucketCount = (range: AnalysisTimeRangeKey) => {
  if (range === '24H') return 24
  if (range === '7D') return 7
  if (range === '30D') return 30
  if (range === '90D' || range === '12M') return 12
  const exhaustive: never = range
  throw new Error(`Unsupported analysis time range: ${exhaustive}`)
}

const startOfIsoWeek = (date: Date) => {
  const start = startOfDay(date)
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7))
  return start
}

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)

const isElementCompleted = (element: TimelineElement) =>
  element.boardStatus === 'COMPLETE' || element.workflowStatus === 'COMPLETE' || Number(element.progress ?? 0) >= 100

const isChecklistCompleted = (item: DerivationChecklistItem) =>
  item.isCompleted || ('workflowStatus' in item && (item.workflowStatus === 'COMPLETED' || item.workflowStatus === 'NOT_REQUIRED'))

const checklistEndDate = (item: DerivationChecklistItem) =>
  item.scheduleEndDate ?? item.scheduleStartDate ?? ('endDate' in item ? item.endDate : null) ?? null

const checklistStartDate = (item: DerivationChecklistItem) =>
  item.scheduleStartDate ?? ('startDate' in item ? item.startDate : null) ?? null

const collectWorkItems = (
  workspaceId: string,
  elements: TimelineElement[],
  includeArchived: boolean,
  resolveMemberLabel?: (uid: string) => string | null,
  checklistItemsInput?: DerivationChecklistItem[],
): WorkItem[] => {
  const workspaceElements = elements.filter((element) => {
    if (element.workspaceId !== workspaceId) return false
    if (includeArchived) return element.board !== 'BIN'
    return element.board !== 'BIN' && element.board !== 'ARCHIVE'
  })
  return workspaceElements.flatMap((element): WorkItem[] => {
    const checklist: DerivationChecklistItem[] = checklistItemsInput
      ? checklistItemsInput.filter(
          (item) =>
            'parentContainerId' in item &&
            item.parentContainerId === element.id &&
            !('board' in item && item.board === 'BIN') &&
            !('deletedAt' in item && item.deletedAt),
        )
      : (element.checklist ?? [])
    const elementItem: WorkItem = {
      id: element.id,
      title: element.title || 'Untitled item',
      type: element.type,
      parentId: element.parentId,
      endDate: element.endDate ?? null,
      startDate: element.startDate ?? null,
      completed: isElementCompleted(element),
      progress: Number(element.progress ?? 0),
      blocked: (element.invalidDependencyNodeIds ?? []).length > 0,
      blockerIds: element.invalidDependencyNodeIds ?? [],
      dependencyIds: element.dependencyNodeIds ?? [],
      assigneeId: null,
      assigneeLabel: null,
      requiresProof: false,
      budgetLimit: Number(element.budgetLimit ?? 0),
      estimatedCost: Number(element.estimatedCost ?? 0),
      stockTouched: checklist.some((item) => (item.stockRequirements ?? []).length > 0 || (item.requiredStockIds ?? []).length > 0),
      formsTouched: (element.attachedFormIds ?? []).length > 0 || checklist.some((item) => (item.attachedFormIds ?? []).length > 0),
      integrationsTouched: Boolean(element.providerMappings || element.mapProviderId),
      priority: Number(element.priority ?? 0),
      lastUpdatedAt: toDate(element.lastUpdatedAt),
      aiTouched: Boolean(element.statusSyncOrigin?.toLowerCase().includes('ai')),
      sourceElementId: element.id,
    }
    const checklistItems = checklist.map((item): WorkItem => {
      const assigneeLabel = item.assigneeId ? resolveMemberLabel?.(item.assigneeId)?.trim() || null : null
      const title = ('text' in item && item.text) || ('title' in item && item.title) || 'Checklist item'
      return {
      id: `${element.id}:${item.id}`,
      title,
      type: 'CHECKLIST',
      parentId: element.id,
      endDate: checklistEndDate(item),
      startDate: checklistStartDate(item),
      completed: isChecklistCompleted(item),
      progress: isChecklistCompleted(item) ? 100 : 0,
      blocked: (item.dependencyNodeIds ?? []).length > 0 || (item.blockedByNodeIds ?? []).length > 0,
      blockerIds: [
        ...(item.dependencyNodeIds ?? []),
        ...(item.blockedByNodeIds ?? []).map((blockedBy: { targetId: string }) => blockedBy.targetId),
      ],
      dependencyIds: item.dependencyNodeIds ?? [],
      assigneeId: item.assigneeId ?? null,
      assigneeLabel,
      requiresProof: Boolean(item.requiresProofFile),
      budgetLimit: Number(item.budgetLimit ?? 0),
      estimatedCost: Number(item.estimatedCost ?? item.actualSpend ?? item.actualCost ?? 0),
      stockTouched: (item.stockRequirements ?? []).length > 0 || (item.requiredStockIds ?? []).length > 0,
      formsTouched: (item.attachedFormIds ?? []).length > 0,
      integrationsTouched: false,
      priority: Number(item.priority ?? item.urgencyStars ?? 0),
      lastUpdatedAt: toDate('assignedAt' in item ? item.assignedAt : null),
      aiTouched: false,
      sourceElementId: element.id,
      }
    })
    return [elementItem, ...checklistItems]
  })
}

export const computeHealthScore = (items: WorkItem[], now: Date = new Date()): AnalysisHealth => {
  const total = Math.max(1, items.length)
  const completed = items.filter((item) => item.completed).length
  const blocked = items.filter((item) => item.blocked && !item.completed).length
  const overdue = items.filter((item) => !item.completed && daysLate(item.endDate, now) > 0).length
  const budgeted = items.filter((item) => item.budgetLimit > 0)
  const overBudget = budgeted.filter((item) => item.estimatedCost > item.budgetLimit).length
  const aiTouched = items.filter((item) => item.aiTouched).length
  const subScores: AnalysisHealthSubScores = {
    execution: clampScore((completed / total) * 100),
    blocker: clampScore(100 - (blocked / total) * 120),
    drift: clampScore(100 - (overdue / total) * 120),
    ops: clampScore(100 - (overBudget / Math.max(1, budgeted.length)) * 100),
    ai: clampScore(Math.min(100, aiTouched * 10)),
  }
  const score = clampScore(
    subScores.execution * 0.32
    + subScores.blocker * 0.24
    + subScores.drift * 0.22
    + subScores.ops * 0.14
    + subScores.ai * 0.08,
  )
  return {
    score,
    subScores,
    state: score < 45 ? 'CRITICAL' : score < 72 ? 'WATCH' : 'HEALTHY',
  }
}

export const buildTrendStrip = (
  items: WorkItem[],
  now: Date,
  range: AnalysisTimeRangeKey,
): AnalysisTrend[] => {
  const days = Math.max(1, timeRangeDays(range))
  const bucketLength = range === '90D' || range === '12M' ? bucketCount(range) : Math.min(days, 14)
  const bucketStarts = Array.from({ length: bucketLength }, (_, index) => {
    if (range === '90D') {
      const date = startOfIsoWeek(now)
      date.setDate(date.getDate() - (bucketLength - index - 1) * 7)
      return date
    }
    if (range === '12M') {
      const date = startOfMonth(now)
      date.setMonth(date.getMonth() - (bucketLength - index - 1))
      return date
    }
    const date = startOfDay(now)
    date.setDate(date.getDate() - (Math.min(days, 14) - index - 1))
    return date
  })
  const bucketValue = (predicate: (item: WorkItem, date: Date, index: number) => boolean) =>
    bucketStarts.map((bucket, index) => items.filter((item) => predicate(item, bucket, index)).length)
  const completion = bucketValue((item, bucket) => item.completed && Boolean(item.endDate) && startOfDay(toDate(item.endDate) ?? bucket).getTime() <= bucket.getTime())
  const overdue = bucketValue((item, bucket) => !item.completed && daysLate(item.endDate, bucket) > 0)
  const blocked = bucketValue((item) => item.blocked && !item.completed)
  const activity = bucketValue((item, bucket, index) => {
    if (!item.lastUpdatedAt) return false
    if (range === '90D' || range === '12M') {
      const nextBucket = bucketStarts[index + 1] ?? now
      return item.lastUpdatedAt >= bucket && item.lastUpdatedAt <= now && (index === bucketStarts.length - 1 || item.lastUpdatedAt < nextBucket)
    }
    return startOfDay(item.lastUpdatedAt).getTime() === bucket.getTime()
  })
  const proof = bucketValue((item) => item.requiresProof)
  const forms = bucketValue((item) => item.formsTouched)
  const automation = bucketValue((item) => item.integrationsTouched)
  const makeTrend = (
    id: string,
    label: string,
    points: number[],
    direction: AnalysisTrend['direction'],
  ): AnalysisTrend => ({
    id,
    label,
    current: points.at(-1) ?? 0,
    delta: (points.at(-1) ?? 0) - (points.at(-2) ?? 0),
    points,
    direction,
  })
  return [
    makeTrend('completion', 'Completion', completion, 'up-is-good'),
    makeTrend('overdue', 'Overdue', overdue, 'up-is-bad'),
    makeTrend('blocked', 'Blocked', blocked, 'up-is-bad'),
    makeTrend('activity', 'Activity', activity, 'up-is-good'),
    makeTrend('proof', 'Proof', proof, 'up-is-bad'),
    makeTrend('forms', 'Forms', forms, 'up-is-good'),
    makeTrend('automation', 'Integrations', automation, 'up-is-good'),
  ]
}

export const walkBlockerChains = (items: WorkItem[]): AnalysisBlockerChain[] => {
  const itemById = new Map(items.map((item) => [item.id, item]))
  return items
    .filter((item) => item.blocked && !item.completed)
    .slice(0, 6)
    .map((item) => {
      const affected = item.blockerIds
        .map((id) => itemById.get(id)?.id ?? id)
        .filter(Boolean)
        .slice(0, 25)
      return {
        id: `chain-${item.id}`,
        rootNodeId: item.id,
        title: item.title,
        reason: affected.length ? `${affected.length} prerequisite signal${affected.length === 1 ? '' : 's'} need attention.` : 'Dependency status is unresolved.',
        chainLength: Math.max(1, affected.length + 1),
        affectedNodeIds: affected,
      }
    })
}

export const detectTimelineDrift = (items: WorkItem[], now: Date): AnalysisDriftEvent[] =>
  items
    .filter((item) => !item.completed && daysLate(item.endDate, now) > 0)
    .sort((a, b) => daysLate(b.endDate, now) - daysLate(a.endDate, now))
    .slice(0, 6)
    .map((item) => {
      const driftDays = daysLate(item.endDate, now)
      return {
        id: `drift-${item.id}`,
        nodeId: item.id,
        title: item.title,
        driftDays,
        plannedEnd: item.endDate ?? '',
        severity: driftDays >= 14 ? 'HIGH' : driftDays >= 5 ? 'MEDIUM' : 'LOW',
      }
    })

export const buildReadinessMatrix = (items: WorkItem[]): AnalysisReadinessRow[] =>
  items
    .filter((item) => !item.completed)
    .slice(0, 8)
    .map((item) => {
      const overBudget = item.budgetLimit > 0 && item.estimatedCost > item.budgetLimit
      return {
        id: `ready-${item.id}`,
        title: item.title,
        dependencies: item.blocked ? 'blocked' : item.dependencyIds.length ? 'warn' : 'ready',
        proof: item.requiresProof ? 'warn' : 'ready',
        stock: item.stockTouched ? 'warn' : 'ready',
        budget: overBudget ? 'blocked' : item.budgetLimit > 0 ? 'warn' : 'ready',
      }
    })

export const buildAssigneeLoad = (items: WorkItem[], now: Date): { assigneeLoad: AnalysisAssigneeLoad[]; unassigned: number } => {
  const assigned = new Map<string, AnalysisAssigneeLoad>()
  let unassigned = 0
  for (const item of items.filter((candidate) => candidate.type === 'CHECKLIST')) {
    if (!item.assigneeId) {
      unassigned += 1
      continue
    }
    const current = assigned.get(item.assigneeId) ?? {
      id: item.assigneeId,
      label: item.assigneeLabel ?? `Assignee ${item.assigneeId.slice(0, 6)}`,
      total: 0,
      overdue: 0,
      completed: 0,
    }
    current.total += 1
    if (item.completed) current.completed += 1
    if (!item.completed && daysLate(item.endDate, now) > 0) current.overdue += 1
    assigned.set(item.assigneeId, current)
  }
  return {
    assigneeLoad: Array.from(assigned.values()).sort((a, b) => b.total - a.total).slice(0, 8),
    unassigned,
  }
}

export const rankRecommendations = (
  work: AnalysisSnapshot['work'],
  ops: AnalysisSnapshot['ops'],
  blockerChains: AnalysisBlockerChain[],
  driftEvents: AnalysisDriftEvent[],
): AnalysisRecommendation[] => {
  const recommendations: AnalysisRecommendation[] = []
  if (blockerChains.length > 0) {
    recommendations.push({
      id: 'rec-blockers',
      title: 'Clear dependency pressure',
      reason: `${blockerChains.length} blocked chain${blockerChains.length === 1 ? '' : 's'} are slowing active work.`,
      priority: 'HIGH',
      drillTarget: 'CHECKLISTS',
    })
  }
  if (driftEvents.length > 0) {
    recommendations.push({
      id: 'rec-drift',
      title: 'Review slipping timelines',
      reason: `${driftEvents.length} active item${driftEvents.length === 1 ? '' : 's'} are past planned end dates.`,
      priority: 'HIGH',
      drillTarget: 'DASHBOARD',
    })
  }
  if (ops.proofRequired > 0) {
    recommendations.push({
      id: 'rec-proof',
      title: 'Collect missing proof',
      reason: `${ops.proofRequired} active work item${ops.proofRequired === 1 ? '' : 's'} require proof or approval evidence.`,
      priority: 'MEDIUM',
      drillTarget: 'UPLOAD',
    })
  }
  if (ops.budgeted > 0) {
    recommendations.push({
      id: 'rec-budget',
      title: 'Check cost pressure',
      reason: `${ops.budgeted} item${ops.budgeted === 1 ? '' : 's'} carry budget estimates in the active plan.`,
      priority: 'MEDIUM',
      drillTarget: 'BUDGET',
    })
  }
  if (recommendations.length === 0) {
    recommendations.push({
      id: 'rec-steady',
      title: 'Keep the operating rhythm',
      reason: `${work.active} active item${work.active === 1 ? '' : 's'} are visible with no major local blockers detected.`,
      priority: 'LOW',
      drillTarget: 'DASHBOARD',
    })
  }
  return recommendations.slice(0, 5)
}

export function buildLocalAnalysisSnapshot(workspaceId: string, elements: TimelineElement[], options?: Omit<AnalysisDerivationOptions, 'cohort'> & { cohort?: null }): AnalysisSnapshot
export function buildLocalAnalysisSnapshot(workspaceId: string, elements: TimelineElement[], options: AnalysisDerivationOptions & { cohort: AnalysisCohortSlice }): CohortAnalysisSnapshots
export function buildLocalAnalysisSnapshot(workspaceId: string, elements: TimelineElement[], options: AnalysisDerivationOptions): AnalysisSnapshot | CohortAnalysisSnapshots
export function buildLocalAnalysisSnapshot(
  workspaceId: string,
  elements: TimelineElement[],
  options: AnalysisDerivationOptions = {},
): AnalysisSnapshot | CohortAnalysisSnapshots {
  const now = options.now ?? new Date()
  const items = collectWorkItems(
    workspaceId,
    elements,
    options.includeArchived ?? false,
    options.resolveMemberLabel,
    options.checklistItems,
  )
  const buildSnapshot = (snapshotItems: WorkItem[]): AnalysisSnapshot => {
  const active = snapshotItems.filter((item) => !item.completed)
  const blocked = active.filter((item) => item.blocked)
  const overdue = active.filter((item) => daysLate(item.endDate, now) > 0)
  const stalled = active.filter((item) => {
    if (!item.lastUpdatedAt) return false
    return Math.floor((now.getTime() - item.lastUpdatedAt.getTime()) / MS_PER_DAY) >= 7
  })
  const checklistItems = snapshotItems.filter((item) => item.type === 'CHECKLIST')
  const byType = {
    GOAL: snapshotItems.filter((item) => item.type === 'GOAL').length,
    TASK: snapshotItems.filter((item) => item.type === 'TASK').length,
    SUBTASK: snapshotItems.filter((item) => item.type === 'SUBTASK').length,
    CHECKLIST: checklistItems.length,
  }
  const work: AnalysisSnapshot['work'] = { total: snapshotItems.length, active: active.length, completed: snapshotItems.filter((item) => item.completed).length, overdue: overdue.length, blocked: blocked.length, stalled: stalled.length, checklistTotal: checklistItems.length, checklistCompleted: checklistItems.filter((item) => item.completed).length, byType }
  const blockerChains = walkBlockerChains(snapshotItems)
  const driftEvents = detectTimelineDrift(snapshotItems, now)
  const ops: AnalysisSnapshot['ops'] = { proofRequired: active.filter((item) => item.requiresProof).length, budgeted: active.filter((item) => item.budgetLimit > 0 || item.estimatedCost > 0).length, stockTouched: active.filter((item) => item.stockTouched).length, formsTouched: active.filter((item) => item.formsTouched).length, integrationsTouched: active.filter((item) => item.integrationsTouched).length }
  const team = buildAssigneeLoad(snapshotItems, now)
  return { workspaceId, generatedAt: now, health: computeHealthScore(snapshotItems, now), work, team, ops, governance: { recentlyUpdated: snapshotItems.filter((item) => item.lastUpdatedAt && now.getTime() - item.lastUpdatedAt.getTime() <= MS_PER_DAY).length, highPriority: active.filter((item) => item.priority >= 4).length }, ai: { generatedCandidates: snapshotItems.filter((item) => item.aiTouched).length, lowConfidenceSignals: blocked.length + stalled.length }, trends: buildTrendStrip(snapshotItems, now, options.timeRange ?? '7D'), blockerChains, driftEvents, readinessMatrix: buildReadinessMatrix(snapshotItems), recommendations: rankRecommendations(work, ops, blockerChains, driftEvents) }
  }
  if (!options.cohort) return buildSnapshot(items)
  const unfiltered = buildSnapshot(items)
  const clusters = buildAssigneeClusters(unfiltered.team.assigneeLoad)
  const cohortItems = items.filter((item) => item.assigneeId && (options.cohort?.kind === 'ROLE'
    ? options.memberRoles?.get(item.assigneeId)?.toUpperCase() === options.cohort.value
    : clusters.get(item.assigneeId) === options.cohort?.value))
  return { sidecar: unfiltered, middle: buildSnapshot(cohortItems) }
}
